import { Mail, MapPin, Sparkles, ShieldCheck, Hammer, Award } from "lucide-react";
import Nav from "../components/site/Nav";
import Hero from "../components/site/Hero";
import FloatingActions from "../components/site/FloatingActions";
import BeforeAfter from "../components/site/BeforeAfter";
import VideoGallery from "../components/site/VideoGallery";
import Testimonials from "../components/site/Testimonials";
import AppointmentForm from "../components/site/AppointmentForm";
import useReveal from "../hooks/useReveal";
import {
  BUSINESS,
  IMAGES,
  STONES,
  SERVICES,
  RESIDENTIAL,
  COMMERCIAL,
} from "../lib/site-content";

const ContactLine = () => (
  <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
    <a
      href={BUSINESS.emailHref}
      className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-stone-900 text-stone-900 text-sm tracking-wider hover:bg-stone-100 transition-colors"
    >
      <Mail className="w-4 h-4" /> {BUSINESS.email}
    </a>
  </div>
);

const SectionLabel = ({ num, children }) => (
  <p className="text-[11px] tracking-[0.3em] uppercase text-stone-500 mb-4">
    {num} / {children}
  </p>
);

export default function Landing() {
  const ref = useReveal();

  return (
    <main ref={ref} className="bg-[#f0ece4] text-stone-900 min-h-screen">
      <Nav />
      <Hero />

      {/* ABOUT */}
      <section id="about" className="relative py-16 md:py-20 bg-[#f0ece4]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <SectionLabel num="01">The Stone Remedy</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] text-stone-900">
              A quiet obsession with stone — and the patience it deserves.
            </h2>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-stone-600">
              Every floor, vanity and surface we touch is treated as a single, irreplaceable
              piece of natural history. Our craft is not in covering damage — it is in coaxing
              the stone back to the way it was first cut.
            </p>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-stone-600">
              Two decades of restoration across private estates, hotels, and historic
              landmarks. Diamond-honed, hand-finished, and quietly obsessive about the
              difference between "clean" and <em>correct</em>.
            </p>
            <div className="mt-10">
              <ContactLine />
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] overflow-hidden">
              <img
                src={IMAGES.about}
                alt="Restored marble interior"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-stone-900/5" />
            </div>

          </div>
        </div>
      </section>

      {/* RESIDENTIAL */}
      <section id="residential" className="relative py-16 md:py-20 bg-[#e8e2d8]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1 reveal">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMAGES.residential} alt="Polished nero marble lobby" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2 reveal">
            <SectionLabel num="02">Residential</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] text-stone-900">
              For the homes where details are inherited.
            </h2>
            <p className="mt-5 text-base md:text-lg text-stone-600 leading-relaxed">
              Discreet, white-glove restoration for private residences. We work around your
              household, your designer, and your schedule — quietly and without dust.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {RESIDENTIAL.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm text-stone-700">
                  <span className="mt-1.5 w-1 h-1 bg-stone-900 rounded-full shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <ContactLine />
            </div>
          </div>
        </div>
      </section>

      {/* COMMERCIAL */}
      <section id="commercial" className="relative py-16 md:py-20 bg-stone-900 text-stone-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 reveal">
            <p className="text-[11px] tracking-[0.3em] uppercase text-stone-400 mb-4">03 / Commercial</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.1] text-white">
              First impressions, polished to the last vein.
            </h2>
            <p className="mt-5 text-base md:text-lg text-stone-300 leading-relaxed">
              Hotels, restaurants, condominiums, and corporate flagships. We work overnight,
              behind a barricade, or in step with your operations team — never in the way of
              your guests.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {COMMERCIAL.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-stone-300">
                  <span className="mt-1.5 w-1 h-1 bg-stone-400 rounded-full shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a href={BUSINESS.emailHref} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-stone-900 text-sm tracking-wider hover:bg-stone-100 transition-colors">
                <Mail className="w-4 h-4" /> Request Proposal
              </a>
            </div>
          </div>
          <div className="lg:col-span-6 reveal">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={IMAGES.commercial} alt="Commercial marble lobby" className="w-full h-full object-cover grayscale-[0.15]" />
            </div>
          </div>
        </div>
      </section>

      {/* STONES */}
      <section id="stones" className="relative py-16 md:py-20 bg-[#f0ece4]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal max-w-3xl">
            <SectionLabel num="04">The Materials We Speak</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-stone-900">
              Stones we know intimately.
            </h2>
            <p className="mt-5 text-base md:text-lg text-stone-600 leading-relaxed">
              Each stone has a temperament. Carrara forgives; onyx does not. Knowing the
              difference is the difference.
            </p>
          </div>
          <div className="reveal mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px bg-stone-200 border border-stone-200">
            {STONES.map((s) => (
              <div key={s.name} className="bg-[#f0ece4] p-6 hover:bg-[#e8e2d8] transition-colors group">
                <p className="font-serif text-lg md:text-xl text-stone-900 leading-tight">{s.name}</p>
                <p className="mt-2 text-xs text-stone-500 leading-relaxed">{s.note}</p>
                <div className="mt-5 h-px w-8 bg-stone-300 group-hover:w-16 group-hover:bg-stone-900 transition-all" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="relative py-16 md:py-20 bg-[#e8e2d8]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="reveal max-w-3xl mb-12">
            <SectionLabel num="05">Services Offered</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-stone-900">
              The full library of restoration.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 border border-stone-200">
            {SERVICES.map((s, idx) => (
              <div key={s.title} className="bg-[#f0ece4] p-7 md:p-9 reveal">
                <div className="flex items-start gap-5">
                  <div className="w-10 h-10 border border-stone-300 flex items-center justify-center shrink-0">
                    {idx % 4 === 0 ? <Sparkles className="w-4 h-4" /> : null}
                    {idx % 4 === 1 ? <ShieldCheck className="w-4 h-4" /> : null}
                    {idx % 4 === 2 ? <Hammer className="w-4 h-4" /> : null}
                    {idx % 4 === 3 ? <Award className="w-4 h-4" /> : null}
                  </div>
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-stone-900 leading-tight">{s.title}</h3>
                    <p className="mt-2 text-sm md:text-base text-stone-600 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal mt-12 text-center">
            <ContactLine />
          </div>
        </div>
      </section>

      <BeforeAfter />
      <VideoGallery />


      {/* SCHEDULE */}
      <section id="schedule" className="relative py-16 md:py-20 bg-[#f0ece4]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="reveal text-center">
            <SectionLabel num="06">Schedule an Appointment</SectionLabel>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-stone-900">
              Tell us about your stone.
            </h2>
            <p className="mt-5 text-base md:text-lg text-stone-600 leading-relaxed">
              On-site consultations are complimentary throughout Westchester, New York City and
              Connecticut. Most requests receive a same-day reply.
            </p>
          </div>
          <div className="reveal mt-12 bg-[#e8e2d8] border border-stone-300 p-6 md:p-10 shadow-sm">
            <AppointmentForm />
          </div>
        </div>
      </section>

      {/* CONTACT / FOOTER */}
      <section id="contact" className="relative py-16 md:py-20 bg-stone-900 text-stone-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-6 reveal">
              <p className="text-[11px] tracking-[0.3em] uppercase text-stone-400 mb-6">07 / Contact</p>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.1]">
                Talk to a craftsman, not a call center.
              </h2>
              <p className="mt-6 text-stone-400 text-base md:text-lg leading-relaxed max-w-md">
                Every inquiry is read and answered by the team that will be on your project.
              </p>
            </div>
            <div className="lg:col-span-6 reveal grid grid-cols-1 sm:grid-cols-2 gap-8">
              <a href={BUSINESS.emailHref} className="group block p-6 border border-stone-700 hover:border-stone-300 transition-colors">
                <Mail className="w-5 h-5 text-stone-400 group-hover:text-white transition-colors" />
                <p className="mt-6 text-[11px] tracking-[0.25em] uppercase text-stone-400">Email</p>
                <p className="mt-2 font-serif text-lg sm:text-xl text-white break-all">{BUSINESS.email}</p>
              </a>
              <div className="sm:col-span-2 p-6 border border-stone-700">
                <MapPin className="w-5 h-5 text-stone-400" />
                <p className="mt-6 text-[11px] tracking-[0.25em] uppercase text-stone-400">Service Area</p>
                <p className="mt-2 font-serif text-lg text-white">Westchester · New York City · Connecticut</p>
                <p className="mt-2 text-sm text-stone-400">
                  By appointment, six days a week. After-hours and overnight commercial work available on request.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-20 pt-8 border-t border-stone-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="font-serif text-stone-200">The Stone Remedy</span>
            <p className="text-xs text-stone-500">
              © {new Date().getFullYear()} The Stone Remedy. Natural stone restoration, by hand.
            </p>
          </div>
        </div>
      </section>

      <FloatingActions />
    </main>
  );
}
